'use client'

import React, { ReactNode, useCallback, useMemo } from 'react'

import { AppContext } from 'Services/AppContext'

import GlobalStyles from 'Theme/GlobalStyles'

import { StoreProvider } from './storeProvider'
import StyleSheetProvider from './StyleSheetProvider'
import ThemeProvider from './ThemeProvider'

declare global {
  interface Window {
    WorklairWidget?: { open: () => void; close: () => void }
  }
}

const WIDGET_WAIT_TIMEOUT_MS = 10000
const WIDGET_POLL_INTERVAL_MS = 100

function waitForSupportWidget() {
  return new Promise<Window['WorklairWidget']>(resolve => {
    const startedAt = Date.now()

    const timer = window.setInterval(() => {
      const timedOut = Date.now() - startedAt > WIDGET_WAIT_TIMEOUT_MS

      if (window.WorklairWidget || timedOut) {
        window.clearInterval(timer)
        resolve(window.WorklairWidget)
      }
    }, WIDGET_POLL_INTERVAL_MS)
  })
}

type Props = { children: ReactNode }

export default function ClientAppProviders({ children }: Props) {
  // Every one of the 13 call sites is an "open the chat" CTA — none is a close
  // button. This used to toggle on a local ref, which broke two ways: the
  // second CTA a visitor clicked closed the widget instead of opening it, and
  // closing the widget by its own X left the ref stale, so the next CTA click
  // called close() on an already-closed widget and appeared dead. The widget
  // owns its own visibility; we only ever ask it to open.
  const handleOpenSupportWidget = useCallback(async () => {
    if (typeof window === 'undefined') return

    // The widget script is `lazyOnload`, so a CTA can be clicked before it has
    // arrived. Previously that click was a silent no-op (the old
    // `afterInteractive` script had the same gap, just a narrower one) — wait
    // for it instead, and give up rather than hang if it never loads.
    const widget = window.WorklairWidget ?? (await waitForSupportWidget())

    if (!widget) return

    widget.open()
  }, [])

  const ctxValue = useMemo(
    () => ({
      me: {}, // fill with real user later
      openSupportWidget: handleOpenSupportWidget,
    }),
    [handleOpenSupportWidget],
  )

  return (
    <StoreProvider>
      <AppContext.Provider value={ctxValue}>
        <StyleSheetProvider>
          <ThemeProvider>
            <GlobalStyles />

            {children}
          </ThemeProvider>
        </StyleSheetProvider>
      </AppContext.Provider>
    </StoreProvider>
  )
}
